// /latentgateway — the model-access component: controls, what the gateway records per
// request, supported model identifiers, and what it does not do.
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { SitePage, Btn, Go, TO } from './SiteChrome';
import { MODEL_GROUPS } from './content';

const BULLETS = ['Private and approved external models', 'Team access and model tiers', 'Permissions distinct from task routing'];

const COUNTERS = [{ v: '48,476', k: 'Requests' }, { v: '9,154,504', k: 'Tokens' }, { v: '$414.16', k: 'Cost' }, { v: '1,308', k: 'PII masked' }];

const WORKS_WITH = [
  { k: 'Models', v: 'Private self-hosted endpoints and approved third-party endpoints exposed over OpenAI-compatible APIs.' },
  { k: 'Agents', v: 'LatentCode, and supported MCP-compatible third-party coding agents configured to route through the gateway.' },
  { k: 'Identity', v: 'Team and role mapping from your existing directory, for access and model tiers.' },
];

const LIMITS = [
  'It does not host or serve models. It governs access to the endpoints you provide or permit.',
  'It does not remove the terms of an external provider. Permitted inputs sent to that provider are handled under that provider’s terms.',
  'It does not route work by permission alone. Team permissions are distinct from task routing.',
  'It does not screen every kind of personal data. L1 pattern and checksum rules can let some through, such as names in free text.',
];

const RELATED = [
  { name: 'LatentCode', to: TO.docsLatentcode, why: 'The execution surface whose model access the gateway governs.' },
  { name: 'Managed agents', to: TO.docsAgents, why: 'Delegated execution runs under the same permitted-model policy.' },
];

const FAQS = [
  { q: 'Can we use both private and third-party models?', a: 'Yes, where your environment and policies permit. Configure supported private models for restricted work and approved third-party models for other workloads. Air-gapped deployments use models available inside the isolated environment.' },
  { q: 'What is screened before routing?', a: 'L1 pattern and checksum rules screen each input. Admins choose to block, mask or allow. Some personal data, such as names in free text, can pass.' },
  { q: 'Does our code leave our network?', a: 'It depends on the configuration you choose. Work restricted to private models has no external fallback. Work you permit to use approved third-party models sends the permitted inputs to that provider under that provider’s terms.' },
];

function GatewayPage() {
  return (
    <SitePage>
      <section className="lsl-wrap lsl-pad" style={{ paddingTop: 'var(--space-11)', paddingBottom: 'var(--space-9)' }}>
        <div className="lsl-crumbs">
          <Go to={TO.stack}>LatentStack</Go><span>/</span><span>LatentGateway</span>
        </div>
        <div className="lsl-comp-hero">
          <div>
            <h1 className="lsl-page-h1" style={{ maxWidth: '20ch', marginBottom: 'var(--space-6)' }}>LatentGateway</h1>
            <p className="lsl-comp-headline">The right model access. For every team.</p>
            <p className="lsl-comp-desc">
              Configure supported endpoints, model tiers and team permissions. Keep restricted work on private models and
              allow approved third-party models for other workloads.
            </p>
            <div className="lsl-hero-ctas">
              <Btn to={TO.contact} iconRight={ArrowRight}>Evaluate this component</Btn>
              <Btn to={TO.docs} variant="outline" iconRight={ArrowRight}>Admin console docs</Btn>
            </div>
            <div className="lsl-comp-meta">LatentStack is a modular AI software stack. This component is available on its own.</div>
          </div>
          <div className="lsl-panel">
            <div className="lsl-label">How it works</div>
            <div className="lsl-panel-list">
              {BULLETS.map((b, i) => (
                <div key={b} className="lsl-numbered"><span>0{i + 1}</span><span>{b}</span></div>
              ))}
            </div>
            <div className="lsl-panel-foot">Illustrative model-access configuration</div>
          </div>
        </div>
      </section>

      <section className="lsl-wrap lsl-sec lsl-sec--tall lsl-stack" style={{ gap: 'var(--space-9)' }}>
        <div className="lsl-gw">
          <div className="lsl-gw-card">
            <div className="lsl-gw-top">
              <span className="lsl-eyebrow">One request at the gateway</span>
              <span className="lsl-delta" style={{ color: '#6B5410', background: '#FBF1CC' }}>Illustrative · not live telemetry</span>
            </div>
            <div className="lsl-gw-log">guardrail L3 · PII scan · 1 email masked → forward clean</div>
            <div className="lsl-gw-counters">
              {COUNTERS.map((c) => (
                <div key={c.k}><strong>{c.v}</strong><span className="lsl-eyebrow">{c.k}</span></div>
              ))}
            </div>
          </div>
          <div className="lsl-gw-note">
            Illustrative figures from a sample period, shown to explain what the gateway records per request: each request
            is priced, logged and attributed by user, project, model and provider. Not live telemetry, and not
            representative of an enterprise deployment's scale.
          </div>
        </div>

        <div>
          <h2 className="lsl-h2-4">Supported model identifiers</h2>
          <div className="lsl-id-groups">
            {MODEL_GROUPS.map((g) => (
              <div key={g.title}>
                <div className="lsl-label">{g.label}</div>
                <div className="lsl-pills">
                  {g.ids.map((id) => <span key={id} className="lsl-pill">{id}</span>)}
                </div>
              </div>
            ))}
          </div>
          <p className="lsl-id-note">
            Selecting a hosted provider sends the permitted inputs to that provider, under that provider's terms. Each
            identifier carries a verification date in the versioned compatibility list.
          </p>
        </div>

        <div className="lsl-panels">
          <div className="lsl-panel">
            <div className="lsl-label">Works with</div>
            <div className="lsl-panel-list">
              {WORKS_WITH.map((w) => (
                <div key={w.k}><div className="lsl-works-k">{w.k}</div><div className="lsl-works-v">{w.v}</div></div>
              ))}
            </div>
          </div>
          <div className="lsl-panel">
            <div className="lsl-label">What it does not do</div>
            <div className="lsl-panel-list lsl-panel-list--tight">
              {LIMITS.map((x) => (
                <div key={x} className="lsl-dash"><span aria-hidden>−</span><span>{x}</span></div>
              ))}
            </div>
          </div>
          <div className="lsl-panel">
            <div className="lsl-label">Deployment note</div>
            <div className="lsl-works-v" style={{ marginTop: 'var(--space-7)' }}>
              Available in all three deployment modes. In an air-gapped configuration there are no external model calls,
              so the gateway governs only locally available models.
            </div>
            <div className="lsl-panel-action">
              <Btn to={TO.deployment} variant="outline" size="sm" iconRight={ArrowRight}>Deployment and control</Btn>
            </div>
          </div>
        </div>
      </section>

      <section className="lsl-wrap lsl-sec lsl-sec--tall">
        <div className="lsl-two-col">
          <div>
            <div className="lsl-label">Works alone. Better with</div>
            <div className="lsl-panel-list lsl-panel-list--tight">
              {RELATED.map((r) => (
                <Go key={r.name} to={r.to} className="lsl-related">
                  <h3>{r.name}</h3>
                  <p>{r.why}</p>
                </Go>
              ))}
            </div>
          </div>
          <div>
            <div className="lsl-label">Questions</div>
            <div className="lsl-panel-list lsl-panel-list--tight">
              {FAQS.map((f) => (
                <div key={f.q} className="lsl-qbox"><h3>{f.q}</h3><p>{f.a}</p></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="lsl-band">
        <div className="lsl-wrap lsl-pad lsl-final">
          <h2 className="lsl-h2-4" style={{ maxWidth: '22ch' }}>Evaluate LatentGateway on one of your repositories.</h2>
          <div><Btn to={TO.evaluation} size="lg" iconRight={ArrowRight}>Request an evaluation</Btn></div>
        </div>
      </section>
    </SitePage>
  );
}

export default GatewayPage;
